import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, RefreshCw, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function TwoFactorVerification({ adminId, email, onSuccess, onBack }) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when complete
    if (value && index === 5 && newCode.every(d => d)) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      handleSubmit(pastedData);
    }
  };

  const handleSubmit = async (codeString) => {
    const fullCode = codeString || code.join('');
    
    if (fullCode.length !== 6) {
      toast.error('Introduce un código de 6 dígitos');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/api/admin/2fa/verify`, {
        admin_id: adminId,
        code: fullCode
      });

      if (response.data.success) {
        toast.success('Verificación exitosa');
        onSuccess(response.data.admin);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md mx-auto"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-emerald-900/50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-emerald-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Verificación 2FA</h2>
        <p className="text-gray-400 text-sm">
          Introduce el código de tu app de autenticación
        </p>
        <p className="text-gray-500 text-xs mt-1">{email}</p>
      </div>

      {/* Code Input */}
      <div className="flex justify-center gap-2 mb-6">
        {code.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={index === 0 ? handlePaste : undefined}
            className={`w-12 h-14 text-center text-2xl font-mono bg-gray-800 border-2 rounded-lg text-white
              focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
              transition-all ${digit ? 'border-emerald-500' : 'border-gray-700'}`}
            disabled={loading}
            data-testid={`2fa-digit-${index}`}
          />
        ))}
      </div>

      {/* Submit Button */}
      <Button
        onClick={() => handleSubmit()}
        disabled={loading || code.some(d => !d)}
        className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-lg"
        data-testid="verify-2fa-submit"
      >
        {loading ? (
          <RefreshCw className="w-5 h-5 animate-spin" />
        ) : (
          'Verificar'
        )}
      </Button>

      {/* Back Button */}
      <button
        onClick={onBack}
        className="w-full mt-4 flex items-center justify-center gap-2 text-gray-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver al inicio de sesión
      </button>

      {/* Help Text */}
      <div className="mt-8 p-4 bg-gray-800/50 rounded-xl">
        <p className="text-xs text-gray-400 text-center">
          ¿No tienes acceso a tu app de autenticación?<br />
          Puedes usar uno de tus códigos de respaldo
        </p>
      </div>
    </motion.div>
  );
}

export default TwoFactorVerification;
